import { useEffect } from "react";
import { useScreenContext } from "../Layout";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as rudderanalytics from "../../lib/rudderstack";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import Logo12min from "../../assets/images/12min-logo.svg";
import BooksLandingBr from "../../assets/images/books-landing-br.png";

const Star: React.FC<{ fill?: string }> = ({ fill }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill={fill || "currentColor"}
    className="inline-block h-4 w-4"
  >
    <path
      fillRule="evenodd"
      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
      clipRule="evenodd"
    />
  </svg>
);

const buildLink = (
  isDirectSale: boolean,
  isRetSale: boolean,
  isAppFunnel: boolean,
  code: string,
  productId: string = "",
  anonymousId: string | undefined,
  hidePix: any,
  hideBillet: any
) => {
  return (isDirectSale || isRetSale || isAppFunnel)
    ? `https://pay.hotmart.com/${productId}?off=${code}&sck=${anonymousId}&checkoutMode=10${
        hidePix ? "&hidePix=1" : ""
      }${hideBillet ? "&hideBillet=1" : ""}`
    : `https://pay.hotmart.com/${productId}?off=${code}&sck=${anonymousId}&hidePix=1&hideBillet=1&checkoutMode=10`;
};

/**
 * UserReview component displays a user's review with their name, review text, and a 5-star rating.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.name - The name of the user.
 * @param {string} props.text - The review text provided by the user.
 * @returns {JSX.Element} A JSX element representing the user's review.
 */
const UserReview: React.FC<{ name: string; text: string }> = ({ name, text }) => (
  <div className="mb-5 rounded-md bg-amber-100/80 p-5 text-left">
    <div className="my-1 flex flex-col md:flex-row mb-2 items-left gap-2">
      <p className="text-gray-900 antialiased">{name}</p>
      <span className="flex items-center">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={`start-a-${i}`} fill="rgb(26 86 219)" />
        ))}
      </span>
    </div>
    <p className="text-gray-800 antialiased">{text}</p>
  </div>
);

function Landing() {
  const { t } = useTranslation();
  const [screenData, setScreenData] = useScreenContext();
  const [searchParams] = useSearchParams();
  const { offerType } = useParams();
  const newLifetimeCheckout = useFeatureIsOn("lifetime-checkout-feature");

  const isDirectSale = offerType === "direct";
  const isRetSale = offerType === "ret";
  const isAppFunnel = offerType === "app";

  const offerCode = searchParams.get("code");
  const headline = searchParams.get("headline");
  const subheadline = searchParams.get("subheadline");
  const fromPrice = searchParams.get("fromPrice");
  const toPrice = searchParams.get("toPrice");
  const save = searchParams.get("save");
  const productId = searchParams.get("productId");
  const hidePix = searchParams.get("hidePix");
  const hideBillet = searchParams.get("hideBillet");
  const token = searchParams.get("token");
  const hideBooks = searchParams.get("hideBooks");
  const ctaText = searchParams.get("ctaText");
  const priceSubheadline = searchParams.get("priceSubheadline");

  const anonymousId = rudderanalytics.getAnonymousId();

  const checkoutUrl = newLifetimeCheckout
    ? `https://checkout.12min.com/?tk=${token}&plan=lifetime&offer=93f`
    : buildLink(
        isDirectSale,
        isRetSale,
        isAppFunnel,
        offerCode || "03yukzfr",
        productId || "P5327674P",
        anonymousId,
        hidePix,
        hideBillet
      );

  useEffect(() => {
    setScreenData(() => ({
      ...screenData,
      submitLabel: false,
      nextPath: "book-suggestion/1",
      progress: 100,
    }));
  }, []);

  return (
    <>
      <div className="toaster"></div>
      <header className="header bg-white py-7">
        <div className="header__container w-full max-w-5xl px-9 mx-auto flex justify-between items-center">
          <div className="header__left">
            <a href="#/" className="header__logo">
              <img
                src={Logo12min}
                alt="12min Logo"
                className="w-32 h-6"
              />
            </a>
          </div>
          <div className="header__right"></div>
        </div>
      </header>
      <main className="mb-20">
        <div className="coupon-hero py-12 l:pb-16 bg-red-100">
          <div className="container w-full max-w-5xl px-9 mx-auto flex flex-col items-center">
            
            <div className="flex justify-center items-center mb-3 px-2 rounded bg-red-500 p-1 text-white text-sm antialiased">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mr-1 inline-block h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t("landing_c.badge")}
            </div>

            <h1 className="my-5 text-5xl md:text-6xl text-center font-bold leading-[3.75rem] md:leading-tight text-red-500">
              {headline || t("landing_c.headline")}
            </h1>
            <h2 className="text-center w-full max-w-xl text-h5 mb-27 md:mb-8 font-normal text-slate-900 antialiased">
              {subheadline || t("landing_c.subheadline")}
            </h2>

            {!hideBooks && (
              <img
                src={BooksLandingBr}
                className="hidden md:block mb-8 min-h-[8rem] w-full max-w-[29rem] mx-auto px-2"
                alt="5 book covers"
              />
            )}

            <div className={"mt-10 border-4 border-blue-700 rounded text-gray-900"}>
              <div className=" bg-gray-100 p-4 py-6">
                <div>
                  <div className=" -mt-10 mb-2 w-32  bg-blue-700 py-1 px-2 text-center text-xs rounded text-white">
                    {t("landing_c.save_text")} {save || t("landing_c.save_offer")}
                  </div>
                  <b className="mt-5 block text-lg text-center">12MIN PREMIUM</b>
                  <div className="py-2 text-center">
                    <span className="mr-2 inline-block font-light text-red-600 line-through">
                      {fromPrice || t("landing_c.old_price")}
                    </span>
                    <b className="inline-block text-xl">
                      {toPrice || t("landing_c.new_price")}
                    </b>
                  </div>
                  <b className="block text-sm text-blue-700 text-center">
                    {priceSubheadline || t(
                      `landing_c.${
                        (isDirectSale || isRetSale || isAppFunnel) ? "direct_" : ""
                      }sale_caption`
                    )}
                  </b>

                  <Link
                    to={checkoutUrl}
                    onClick={() => rudderanalytics.checkoutStarted()}
                  >
                    <button
                      type="button"
                      className="text-md mt-5 w-full rounded-md bg-blue-700 px-5 py-2.5 font-normal text-white hover:bg-blue-800 focus:ring-blue-300"
                    >
                      {ctaText || t(
                        (isDirectSale || isRetSale || isAppFunnel)
                          ? "landing_c.direct_cta_text"
                          : "landing_c.cta_text"
                      )}
                    </button>
                  </Link>
                </div>
              </div>

              <div className="bg-white p-4 text-left">
                <div className="mt-2 text-left text-sm">
                  <div className="mb-3">
                    <p className="align-middle">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="inline-block h-5 w-6 text-blue-700"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t("landing_c.feature_1", {
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      ></span>
                    </p>
                  </div>

                  <div className="mb-3">
                    <p className="align-middle">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="inline-block h-5 w-6 text-blue-700 "
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t("landing_c.feature_2", {
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      ></span>
                    </p>
                  </div>

                  <div className="mb-3">
                    <p className="align-middle">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="inline-block h-5 w-6 text-blue-700 "
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t("landing_c.feature_3", {
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      ></span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clusters */}

        <div className="app-reviews bg-white py-4">
          <div className="app-reviews__container w-full max-w-5xl flex flex-col items-center mx-auto px-9">
            <h1 className="text-center mb-12 mt-5 text-2xl font-semibold leading-tight tracking-normal text-gray-900  md:text-3xl">
              {t("book_list.reviews.headline")}
            </h1>

            <div className="app-reviews__user-reviews w-full max-w-xl">
              <UserReview name={t("book_list.reviews.review_1.name")} text={t("book_list.reviews.review_1.text")} />
              <UserReview name={t("book_list.reviews.review_2.name")} text={t("book_list.reviews.review_2.text")} />
              <UserReview name={t("book_list.reviews.review_3.name")} text={t("book_list.reviews.review_3.text")} />
            </div>

            <div className="app-reviews__cta flex justify-center mb-0 m:mb-10 w-full">
              <Link to={checkoutUrl}>
                <button
                  type="button"
                  className="text-md my-4 w-full rounded-md bg-blue-700 px-20 py-2.5 font-normal text-white hover:bg-blue-800 focus:ring-blue-300"
                >
                  {ctaText || t(
                    (isDirectSale || isRetSale || isAppFunnel) ? "landing_c.direct_cta_text" : "landing_c.cta_text"
                  )}
                </button>
              </Link>
            </div>
          </div>
        </div>

        <div className="app-stats bg-white py-4">
          <div className="app-stats__container w-full max-w-5xl flex flex-col items-center mx-auto px-9">
            <h1 className="my-10 text-3xl font-semibold leading-tight tracking-normal text-gray-900  md:text-4xl">
              {t("book_list.statistics.headline")}
            </h1>

            <div className="grid gap-x-8 gap-y-8 md:gap-y-2 grid-cols-1 md:grid-cols-3">
              <div className="mb-5 text-center rounded-md bg-blue-100 p-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="inline-block h-10 w-10"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0"
                  />
                </svg>
                <h2 className="my-4 text-3xl font-bold leading-tight tracking-normal text-gray-900  md:text-4xl">
                  {t("book_list.statistics.block_1.headline")}
                </h2>
                <p className="text-gray-800 antialiased">{t("book_list.statistics.block_1.text")}</p>
              </div>
              <div className="mb-5 text-center rounded-md bg-blue-100 p-8">
                <div className="my-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={`start-d-${i}`} />
                  ))}
                </div>
                <h2 className="my-4 text-3xl font-bold leading-tight tracking-normal text-gray-900  md:text-4xl">
                  {t("book_list.statistics.block_2.headline")}
                </h2>
                <p className="text-gray-800 antialiased">{t("book_list.statistics.block_2.text")}</p>
              </div>
              <div className="mb-5 text-center rounded-md bg-blue-100 p-8">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="inline-block h-10 w-10"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                  />
                </svg>

                <h2 className="my-4 text-3xl font-bold leading-tight tracking-normal text-gray-900  md:text-4xl">
                  {t("book_list.statistics.block_3.headline")}
                </h2>
                <p className="text-gray-800 antialiased">{t("book_list.statistics.block_3.text")}</p>
              </div>
            </div>

            <p className="text-sm text-gray-400">{t("book_list.statistics.info")}</p>

            <Link to={checkoutUrl}>
              <button
                type="button"
                className="text-md my-6 w-full rounded-md bg-blue-700 px-20 py-2.5 font-normal text-white hover:bg-blue-800 focus:ring-blue-300"
              >
                {ctaText || t(
                  (isDirectSale || isRetSale || isAppFunnel) ? "landing_c.direct_cta_text" : "landing_c.cta_text"
                )}
              </button>
            </Link>
          </div>
        </div>  

        <div className="app-rule bg-white mt-4 py-4">
          <div className="app-rule__container w-full max-w-5xl flex flex-col items-center mx-auto px-9">
            <p className="text-caption text-center max-w-2xl mx-auto mb-12 antialiased md:mb-16 px-4">
              {t("landing_c.rule")}
            </p>
          </div>
        </div>
        
      </main>
    </>
  );
}

export default Landing;
